import { Link } from 'react-router-dom';
import { getOnboardingSectionPath } from '@/lib/profileOnboarding';
import type { ProfileCompletionSectionId } from '@/lib/profileCompletion';
import { WorklynxIcon } from '@/components/icons/WorklynxIcon';

interface ProfileCompletionBannerProps {
  section: ProfileCompletionSectionId | null;
}

/**
 * Shown while an owner sets their organisation up with their own profile still
 * incomplete. Payroll, approvals and document pages remain gated.
 *
 * A solid amber bar with dark text, the same in light and dark mode: it says what is locked
 * and how to unlock it, so it has to be read, not just noticed.
 */
export function ProfileCompletionBanner({ section }: ProfileCompletionBannerProps) {
  return (
    <>
      {/* Room to scroll the last of the page out from under the bar. On a phone the bar sits
          above the helper button in the corner, so it needs more. */}
      <div aria-hidden="true" className="h-56 sm:h-24" />
      <div
        role="status"
        className="fixed inset-x-3 bottom-20 z-40 mx-auto max-w-3xl animate-in fade-in-0 slide-in-from-bottom-4 rounded-xl bg-amber-300 px-4 py-3 text-slate-950 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.55)] ring-1 ring-amber-500/70 sm:inset-x-6 sm:bottom-3"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-start gap-3 text-sm font-medium leading-snug">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-950/10">
              <WorklynxIcon name="lock" size={16} />
            </span>
            <span className="pt-0.5">
              <strong className="block font-semibold">Finish your personal details to unlock payroll, approvals and documents.</strong>
              <span className="text-slate-800">You can keep setting up your organisation in the meantime.</span>
            </span>
          </p>
          <Link
            to={getOnboardingSectionPath(section)}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:ring-offset-amber-300"
          >
            Complete profile
            <WorklynxIcon name="arrow-right" size={16} />
          </Link>
        </div>
      </div>
    </>
  );
}
